import { ApiError } from "api/client";
import { deleteTree } from "api/trees";
import { useDialogFocus } from "hooks/useDialogFocus";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";

import type { DeleteTreeDialogProps } from "./deleteTreeDialog.types";

import styles from "./DeleteTreeDialog.module.css";

/**
 * Confirmation for deleting a tree. Deletion is a soft delete server-side, so the copy says the
 * tree is recoverable through support rather than implying it is destroyed — but it still spells
 * out what the owner loses immediately (entities, collaborator access, links), because from
 * everyone else's point of view the tree is gone.
 */
const DeleteTreeDialog = ({ entityCount, onCancel, onDeleted, treeId, treeTitle }: DeleteTreeDialogProps) => {
	const { t } = useTranslation("homePage");
	const { getAccessToken } = useSession();
	const dialogRef = useDialogFocus<HTMLDivElement>();

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	// The edit dialog underneath suppresses its own Escape handler while this is open, so Escape
	// backs out of the confirmation only — and never abandons a delete already in flight.
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.key === "Escape" && !isSubmitting) {
				onCancel();
			}
		};

		document.addEventListener("keydown", onKeyDown);

		return () => document.removeEventListener("keydown", onKeyDown);
	}, [isSubmitting, onCancel]);

	const onConfirm = useCallback(async (): Promise<void> => {
		if (isSubmitting) {
			return;
		}

		setErrorMessage("");
		setIsSubmitting(true);

		try {
			const token = await getAccessToken();
			if (!token) {
				setIsSubmitting(false);

				return;
			}

			await deleteTree(treeId, token);
			onDeleted();
		} catch (error) {
			setErrorMessage(error instanceof ApiError ? error.message : t("deleteDialog.genericError"));
			setIsSubmitting(false);
		}
	}, [getAccessToken, isSubmitting, onDeleted, t, treeId]);

	return (
		<div className={styles.overlay}>
			<div
				ref={dialogRef}
				aria-labelledby="delete-tree-title"
				aria-modal="true"
				className={styles.dialog}
				role="alertdialog">
				<header className={styles.header}>
					<h2 id="delete-tree-title">{t("deleteDialog.title")}</h2>
					<p>{t("deleteDialog.description", { title: treeTitle })}</p>
				</header>

				<ul className={styles.consequences}>
					{entityCount > 0 && <li>{t("deleteDialog.consequenceEntities", { count: entityCount })}</li>}
					<li>{t("deleteDialog.consequenceCollaborators")}</li>
					<li>{t("deleteDialog.consequenceLinks")}</li>
					<li className={styles.reassurance}>{t("deleteDialog.consequenceRecoverable")}</li>
				</ul>

				{errorMessage && <p className={styles.error}>{errorMessage}</p>}

				<footer className={styles.footer}>
					<button className={styles.cancelButton} disabled={isSubmitting} onClick={onCancel} type="button">
						{t("deleteDialog.cancel")}
					</button>
					<button className={styles.dangerButton} disabled={isSubmitting} onClick={onConfirm} type="button">
						{isSubmitting ? t("deleteDialog.deleting") : t("deleteDialog.confirm")}
					</button>
				</footer>
			</div>
		</div>
	);
};

export default DeleteTreeDialog;
